// Utilities
import { defineStore } from 'pinia';
import i18n from '@/plugins/i18n';
import { storeToRefs } from 'pinia';

import { mainAxios as axios, noAuthAxios } from '@/assets/js/axios_config';

import _ from 'lodash';

let router;
async function loadRouter() {
    if (import.meta.env.MODE !== 'lib') {
        const module = await import('@/router');
        router = module.default;
    }
}
// Call the async function to load the router
loadRouter().then((r) => {});

import { consume_stream } from '@/assets/js/steamapi';
import { toast } from 'vue3-toastify';
import { useUserStore } from '@/stores/user';
import { role_values } from '@/assets/js/constants';
import { defaultErrorHandling } from '@/assets/js/utils';
import { useProcessStore } from '@/stores/process';

const Status = {
    LOADING: 'LOADING',
    START_PAGE: 'START_PAGE',
    EXPERT_VIEW: 'EXPERT_VIEW',
};

const useExpertStore = defineStore('expert', {
    state: () => ({
        status: Status.START_PAGE,
        user: useUserStore(),

        // Start Page
        dashboard_experts: [],
        user_experts: [],
        invited_experts: [],
        group_experts: [],
        workspace_experts: [],
        loaded_expert: undefined,
        is_loading_dashboard: false,
        is_loading_expert: false,

        // Expert View
        user_role: undefined,
        shared_expert: false,
        expert_id: undefined,
        expert_name: undefined,
        workspace_logo: undefined,
        knowledge_pieces: [],
        example_questions: [],
        used_context: [],
        key_word_results: [],
        documents: [],
        websites: [],
        sas_urls: {},

        // System Diagnostics
        system_prompt: '',
        user_prompt: '',
        cited_sources: [],
        filtered_used_context: [],
        debug: false,
        filter_information: {},

        // manage expert
        fileUploadProgress: 0,
        selected_sources: {},
    }),
    getters: {
        get_dashboard_experts: (state) => state['dashboard_experts'],
        get_invited_experts: (state) => state['invited_experts'],
        get_group_experts: (state) => state['group_experts'],
        get_loaded_expert: (state) => state['loaded_expert'],

        // expert view getters
        get_expert_id: (state) => state['expert_id'],
        is_shared_expert: (state) => state['shared_expert'],
        get_user_role: (state) => state['user_role'],
        get_user_role_value: (state) => {
            if (state['user_role'] === undefined) return 0;
            return role_values[state['user_role']];
        },
        get_expert_name: (state) => state['loaded_expert']?.['name'],
        get_workspace_logo: (state) => state['workspace_logo'],
        get_expert_description: (state) => state['loaded_expert']?.['description']?.trim(),
        get_example_questions: (state) => state['example_questions'],
        get_used_context: (state) => state['used_context'],
        get_key_word_results: (state) => state['key_word_results'],
        get_sharing_id: (state) => {
            if (state['loaded_expert'] === undefined) return null;
            return state['loaded_expert'].sharable_link;
        },
        get_sharing_link_expire_date: (state) => {
            if (state['loaded_expert'] === undefined) return null;
            return state['loaded_expert'].sharable_link_expiration;
        },
        user_has_expert_with_sources: (state) => {
            return state['dashboard_experts'].some((expert) => expert.hasKnowledgebase);
        },
        get_sas_url: (state) => (source_id) => {
            return state['sas_urls'][source_id];
        },
        get_selected_sources_count: (state) => {
            return Object.values(state['selected_sources']).flat().length;
        },
        get_expert_workspace_id: (state) => {
            return state['loaded_expert']?.workspace_id;
        },

        // System Diagnostics
        get_system_prompt: (state) => state['system_prompt'],
        get_user_prompt: (state) => state['user_prompt'],
        get_cited_sources: (state) => state['cited_sources'],
        get_filtered_used_context: (state) => state['filtered_used_context'],
        get_filter_information: (state) => state['filter_information'],

        // overview status getters
        is_loading: (state) => state['status'] === Status.LOADING,
        is_start_page: (state) => state['status'] === Status.START_PAGE,
        is_expert_view: (state) => state['status'] === Status.EXPERT_VIEW,

        // permissions
        has_editor_rights(getters) {
            return this.user.is_logged_in && getters.get_user_role_value >= role_values.EDITOR;
        },
        has_admin_rights(getters) {
            return this.user.is_logged_in && getters.get_user_role_value >= role_values.ADMIN;
        },
        dashboard_experts_by_id: (state) => {
            return state['dashboard_experts'].reduce((acc, expert) => {
                acc[expert.expert_id] = expert;
                return acc;
            }, {});
        },
    },
    actions: {
        // Simple setters
        set_sharing_link(link) {
            this.loaded_expert.sharable_link = link;
        },
        set_sharing_link_expiration(expiration) {
            this.loaded_expert.sharable_link_expiration = expiration;
        },
        set_used_context(used_context) {
            this.used_context = used_context;
        },
        set_key_word_results(key_word_results) {
            this.key_word_results = key_word_results;
        },
        set_filter_information(filter_information) {
            this.filter_information = filter_information;
        },
        set_user_prompt(user_prompt) {
            this.user_prompt = user_prompt;
        },
        set_system_prompt(system_prompt) {
            this.system_prompt = system_prompt;
        },
        set_cited_sources(cited_sources) {
            this.cited_sources = cited_sources;
        },
        set_filtered_used_context(filtered_used_context) {
            this.filtered_used_context = filtered_used_context;
        },

        save_last_selected_expert(expertData) {
            localStorage.setItem('last_selected_expert', JSON.stringify(expertData));
        },

        initiate_chat() {
            this.save_last_selected_expert({
                name: this.loaded_expert.name,
                expert_id: this.loaded_expert.expert_id,
                hasKnowledgebase: this.loaded_expert.hasKnowledgebase,
            });
            this.expert_id = this.loaded_expert.expert_id;
        },

        async reset_state(re_route = true) {
            this.status = Status.START_PAGE;
            this.example_questions = [];
            this.used_context = [];
            this.prompt = '';
            this.cited_sources = [];
            if (re_route) await router.push('/expert');
        },

        async reset_loaded_expert() {
            this.loaded_expert = undefined;
            this.expert_name = undefined;
            this.expert_id = undefined;
            this.knowledge_pieces = [];
            this.example_questions = [];
            this.used_context = [];
            this.documents = [];
            this.websites = [];
            this.user_role = undefined;
            this.shared_expert = false;
        },

        // Backend API Calls
        async cancel_crawl() {
            this.status = Status.START_PAGE;
            await router.push('/');
            // todo add cancel crawl to backend
            // axios.get(`/expert/cancel_crawl`)
        },
        expert_status() {
            const processStore = useProcessStore();
            const { processes } = storeToRefs(processStore);
            if (!this.loaded_expert?.hasKnowledgebase && processes.length === 0) {
                return 'incomplete';
            }

            const has_unfinished_processes =
                Object.values(processes.value).filter(
                    (process) =>
                        ![
                            'website_added',
                            'document_added',
                            'confluence_connected',
                            'confluence_no_documents_found',
                            'confluence_no_spaces_found',
                            'confluence_process_failed',
                            'website_no_data_error',
                            'document_no_data_error',
                            'website_unexpected_error',
                            'document_unexpected_error',
                            'no_pages_to_vectorize',
                        ].includes(process.status),
                ).length > 0;
            if (
                has_unfinished_processes ||
                (processes.length > 0 && !this.loaded_expert.hasKnowledgebase)
            ) {
                return 'processing';
            }

            return 'ready';
        },

        async create_expert(expertName) {
            this.status = Status.LOADING;

            let created_expert = null;
            await axios
                .post(`/expert/create/`, { name: expertName })
                .then((response) => {
                    this.expert_id = response.data.expert_id;
                    created_expert = response.data;
                    const expert = {
                        ...response.data,
                        created_on: new Date().toISOString(),
                        hasKnowledgebase: false,
                        hasActiveProcesses: false,
                        role: 'owner',
                    };
                    this.dashboard_experts = [expert, ...this.dashboard_experts];
                })
                .catch(defaultErrorHandling());
            return created_expert;
        },

        async add_file_request(form_data, onSuccess, onUploadComplete = () => {}) {
            form_data.append('expert_id', this.expert_id);
            form_data.append('debug', this.debug);

            await axios
                .post(`/expert/create/add_files`, form_data, {
                    headers: {
                        'content-type': 'multipart/form-data', // do not forget this
                    },
                    onUploadProgress: (progressEvent) => {
                        this.fileUploadProgress = Math.round(progressEvent.progress * 100);
                        if (this.fileUploadProgress === 100) {
                            onUploadComplete();
                        }
                    },
                })
                .then((response) => {
                    onSuccess(response.data);
                })
                .catch((err) => {
                    if (err.response === undefined) {
                        console.error(err.response);
                        throw new Error('An unexpected error occurred. Please try again later.');
                    } else {
                        throw err;
                    }
                })
                .finally(() => {
                    this.fileUploadProgress = 0;
                });
        },

        async get_initial_question_stream(re_route = true, language = 'auto') {
            const initial_example_questions = [...this.example_questions];
            await consume_stream({
                url: `${import.meta.env.VITE_BACKEND_URL}/v1/expert/generate_questions`,
                data: {
                    expert_id: this.expert_id,
                    language: language,
                },
                auth_token: this.user.get_session_token,
                x_origin: this.user.get_origin,
                initialData: (data) => {
                    // If any data gets sent alongside the text stream will be handled here
                    this.status = Status.EXPERT_VIEW;
                    if (re_route) router.push(`/expert/${data['expert_id']}`);
                    this.used_context = data.used_context;
                    this.prompt = data.prompt;
                },
                dataStream: (text) => {
                    this.example_questions = [...initial_example_questions, text];
                },
            }).catch((error) => {
                toast.error(error.message);
                this.status = Status.START_PAGE;
            });
        },

        async update_expert({ expert_name, expert_description, workspace_visibility }) {
            const { t } = i18n.global;

            // check if name or description changed
            if (
                expert_name === this.loaded_expert.name &&
                expert_description === this.loaded_expert.description &&
                workspace_visibility === this.loaded_expert.workspace_visibility
            ) {
                return;
            }
            // proceed to save changes
            await axios
                .put(`/expert/crud/${this.loaded_expert.expert_id}`, {
                    name: expert_name,
                    description: expert_description,
                    workspace_visibility: workspace_visibility,
                })
                .then(() => {
                    this.loaded_expert.description = expert_description;
                    this.loaded_expert.workspace_visibility = workspace_visibility;
                    this.update_expert_name(this.loaded_expert?.expert_id, expert_name);
                    toast.success(t('alerts.success.changes_saved'));
                })
                .catch((error) => {
                    this.expert_name = this.loaded_expert.name;
                    this.expert_description = this.loaded_expert.description;
                    // if error code 401 create toast
                    if (error?.response?.status === 401) {
                        toast.error(t('alerts.error.not_authorized_to_edit_expert'));
                    } else {
                        toast.error(t('alerts.error.failed_to_save_changes'));
                    }
                });
        },

        async update_expert_options({ no_rag_fallback }) {
            const { t } = i18n.global;
            console.log(no_rag_fallback);
            try {
                await axios.patch(`/expert/crud/options/${this.loaded_expert?.expert_id}`, {
                    no_rag_fallback: no_rag_fallback,
                });
                toast.success(t('manage_expert.settings_lower.options_success'));
            } catch (error) {
                toast.error(t('manage_expert.settings_lower.options_error'));
            }
        },

        async delete_expert(expert_id) {
            return await axios
                .delete(`/expert/crud/${expert_id}`)
                .then(() => {
                    // reset loaded expert if it is the one that got deleted
                    if (this.expert_id === expert_id) {
                        this.reset_loaded_expert();
                    }
                    // remove deleted expert from list of experts
                    this.dashboard_experts = this.dashboard_experts.filter(
                        (expert) => expert.expert_id !== expert_id,
                    );

                    const lastSelectedExpert = JSON.parse(
                        localStorage.getItem('last_selected_expert'),
                    );
                    if (lastSelectedExpert !== null && lastSelectedExpert.expert_id === expert_id) {
                        localStorage.removeItem('last_selected_expert');
                    }
                    toast.success('Expert was successfully deleted');
                    return { deleted: true };
                })
                .catch((err) => {
                    if (err.response.data.detail) {
                        toast.error(err.response.data.detail);
                    } else {
                        toast.error('An unexpected error occurred. Please try again later.');
                    }
                });
        },

        async leave_expert(expert_id, user_id) {
            return await axios
                .delete(`/permission/access/${expert_id}/${user_id}`)
                .then(() => {
                    // reset loaded expert if it is the one that got left
                    if (this.expert_id === expert_id) {
                        this.reset_loaded_expert();
                    }
                    // remove left expert from list of experts
                    this.dashboard_experts = this.dashboard_experts.filter(
                        (expert) => expert.expert_id !== expert_id,
                    );

                    const lastSelectedExpert = JSON.parse(
                        localStorage.getItem('last_selected_expert'),
                    );
                    if (lastSelectedExpert !== null && lastSelectedExpert.expert_id === expert_id) {
                        localStorage.removeItem('last_selected_expert');
                    }
                    toast.success('You have left the expert successfully');
                    return { deleted: true };
                })
                .catch((err) => {
                    if (err.response.data.detail) {
                        toast.error(err.response.data.detail);
                    } else {
                        toast.error('An unexpected error occurred. Please try again later.');
                    }
                });
        },

        async delete_website(source_id, name) {
            return await axios
                .delete(`/expert/crud/${this.loaded_expert.expert_id}/web/${source_id}`)
                .then(() => {
                    toast.success('Website deleted');
                    return { source_id: source_id, name: name, success: true };
                })
                .catch((err) => {
                    console.error(err);
                    toast.error(`Failed to delete website ${name}.`);
                    return { source_id: source_id, name: name, success: false };
                });
        },

        async delete_document(document_id, name) {
            return await axios
                .delete(`/expert/crud/${this.loaded_expert.expert_id}/doc/${document_id}`)
                .then(() => {
                    toast.success('Document deleted');
                    return { document_id: document_id, name: name, success: true };
                })
                .catch((err) => {
                    if (err.response === undefined) {
                        toast.error(`Failed to delete document ${name}.`);
                        return { document_id: document_id, name: name, success: false };
                    }
                });
        },

        async load_expert(expert, re_route = true, saveForLater = false) {
            try {
                this.loaded_expert = expert;
                this.loaded_expert['hasKnowledgebase'] = expert.has_knowledge;

                if (saveForLater) {
                    this.initiate_chat();
                }
                this.loaded_expert['name'] = expert.name
                    ? expert.name
                    : `Expert ${
                          this.dashboard_experts.findIndex(
                              (e) => e.expert_id === expert.expert_id,
                          ) + 1
                      }`;

                this.expert_id = expert.expert_id;
                this.user_role = expert.role;

                this.example_questions =
                    expert.example_questions?.split(';').map((q) => q.trim()) ?? [];

                if (re_route) await router.push(`/expert-configurator/${expert.expert_id}`);

                this.status = Status.EXPERT_VIEW;
                this.is_loading_expert = false;
                return this.loaded_expert;
            } catch (Error) {
                console.log(Error);
            }
        },

        async load_expert_by_id(
            expert_id,
            re_route = true,
            saveForLater = false,
            edit_view = false,
            widget = false,
        ) {
            if (this.loaded_expert?.expert_id !== expert_id) {
                this.reset_loaded_expert();
            }
            this.expert_id = expert_id;
            this.status = Status.LOADING;
            this.is_loading_expert = true;
            await axios
                .get(`/expert/crud${edit_view ? '/edit' : ''}/${expert_id}`)
                .then((response) => {
                    return this.load_expert(response.data, re_route, saveForLater);
                })
                .catch((err) => {
                    if (err.response === undefined) {
                        toast.error('An unexpected error occurred. Please try again later.');
                    } else {
                        toast.error(err.response.data.detail);
                    }
                    // remove expert from local storage if it was not found
                    if (!widget) localStorage.removeItem('last_selected_expert');
                    if (re_route) router.replace('/home');
                    this.status = Status.START_PAGE;
                    this.expert_id = undefined;
                });
        },

        async load_by_sharing_id(sharing_id) {
            this.reset_state(false);
            this.status = Status.LOADING;
            this.example_questions = [];
            await noAuthAxios
                .get(`/expert/utils/share/${sharing_id}`)
                .then((response) => {
                    this.shared_expert = true;
                    this.workspace_logo = response.data.workspace_logo;
                    this.load_expert(response.data, false);
                })
                .catch((err) => {
                    let message = 'An unexpected error occurred. Please try again later.';
                    if (err.response !== undefined) {
                        message = err.response.data.detail;
                    }
                    toast.error(message);
                    router.replace('/');
                    this.status = Status.START_PAGE;
                });
        },

        async load_first_expert_with_sources() {
            const expertWithSources = this.dashboard_experts.find(
                (expert) => expert.hasKnowledgebase,
            );
            if (expertWithSources) {
                this.is_loading_expert = true;
                await this.load_expert_by_id(expertWithSources.expert_id, false, true, true);
                this.is_loading_expert = false;
            }
        },

        async refresh_expert(expert_id) {
            try {
                await this.load_expert_by_id(expert_id, false, false, true);
            } catch (err) {
                if (err.response === undefined) {
                    toast.error(
                        'An unexpected error occurred while updating the expert. Please try reloading the page.',
                    );
                }
            }
        },

        async fetchData() {
            const [dashboardResponse] = await Promise.all([axios.get(`/expert/crud/dashboard/`)]);

            return {
                dashboard: dashboardResponse.data,
            };
        },

        async loadData() {
            this.is_loading_dashboard = true;
            const dont_cache = import.meta.env.VITE_CACHE_BACKEND;

            let data = {};
            if (dont_cache !== 'ON') {
                data = await this.fetchData();
            } else {
                let data = JSON.parse(localStorage.getItem('data'));

                if (!data) {
                    data = await this.fetchData();
                    localStorage.setItem('data', JSON.stringify(data));
                }
            }
            const { dashboard } = data;

            const { group_experts, invited_experts, user_experts, workspace_experts } = dashboard;

            const uniqueExpertIds = new Set();
            const allExperts = [
                ...user_experts,
                ...workspace_experts.map((expert) => ({
                    ...expert,
                    isShared: true,
                    isWorkspace: true,
                })),
                ...group_experts.map((expert) => ({ ...expert, isShared: true, isGroup: true })),
                ...invited_experts.map((expert) => ({
                    ...expert,
                    isShared: true,
                    isInvited: true,
                })),
            ].filter((expert) => {
                if (uniqueExpertIds.has(expert.expert_id)) return false;
                uniqueExpertIds.add(expert.expert_id);
                return true;
            });

            this.dashboard_experts = allExperts
                .sort((a, b) => {
                    if (a.isShared && !b.isShared) return 1;
                    if (!a.isShared && b.isShared) return -1;
                    return b.created_on < a.created_on ? -1 : 1;
                })
                .map((expert, i) => ({
                    ...expert,
                    name: expert.name ? expert.name : `Expert ${i + 1}`,
                    hasKnowledgebase: expert.has_knowledge,
                    hasActiveProcesses:
                        expert.running_processes_count > 0 ||
                        expert.unvectorized_webpages_count > 0,
                }));

            if (!user_experts) this.user_experts = [];
            else this.user_experts = user_experts;

            if (!invited_experts) this.invited_experts = [];
            else this.invited_experts = invited_experts;

            if (!group_experts) this.group_experts = [];
            else this.group_experts = group_experts;

            if (!workspace_experts) this.workspace_experts = [];
            else this.workspace_experts = workspace_experts;

            this.is_loading_dashboard = false;
        },

        update_dashboard_expert() {
            this.dashboard_experts = this.dashboard_experts.map((expert) => {
                if (expert.expert_id === this.expert_id) {
                    return {
                        ...expert,
                        hasKnowledgebase: this.loaded_expert?.hasKnowledgebase,
                        hasActiveProcesses: this.expert_status() === 'processing',
                    };
                }

                return expert;
            });
        },

        update_expert_name(expert_id, name) {
            this.loaded_expert.name = name;
            this.update_dashboard_expert_properties(expert_id, { name: name });
        },

        update_dashboard_expert_properties(expert_id, properties) {
            this.dashboard_experts = this.dashboard_experts.map((expert) => {
                if (expert.expert_id === expert_id) {
                    return {
                        ...expert,
                        ...properties,
                    };
                }

                return expert;
            });
        },

        highlight_expert_as_new(expert_id) {
            this.update_dashboard_expert_properties(expert_id, { isNew: true });
        },

        add_sas_url(source_id, sas_url) {
            this.sas_urls[source_id] = sas_url;
        },

        set_selected_sources(tableId, sources) {
            this.selected_sources[tableId] = sources;
        },

        async delete_multiple_documents(document_ids) {
            return await axios
                .delete(`/expert/crud/${this.loaded_expert.expert_id}/doc`, {
                    data: { document_ids: document_ids },
                })
                .then((response) => {
                    const data = response.data;
                    const deletedCount = data.deleted_document_ids.length;
                    const failedCount = data.failed_document_ids.length;
                    if (deletedCount > 0) {
                        toast.success(
                            `${deletedCount} Document${deletedCount !== 1 ? 's' : ''} deleted`,
                        );
                    }

                    if (failedCount > 0) {
                        toast.error(
                            `${failedCount} Document${failedCount !== 1 ? 's' : ''} failed to delete`,
                        );
                    }

                    return { document_ids: data.deleted_document_ids };
                })
                .catch((err) => {
                    if (err.response === undefined) {
                        toast.error(`Failed to delete documents.`);
                    }
                });
        },

        async delete_multiple_webpages(webpage_ids) {
            return await axios
                .delete(`/expert/crud/${this.loaded_expert.expert_id}/webpage`, {
                    data: { webpage_ids },
                })
                .then((response) => {
                    const data = response.data;
                    const deletedCount = data.deleted_webpage_ids.length;
                    const failedCount = data.failed_webpage_ids.length;
                    if (deletedCount > 0) {
                        toast.success(
                            `${deletedCount} Webpage${deletedCount !== 1 ? 's' : ''} deleted`,
                        );
                    }

                    if (failedCount > 0) {
                        toast.error(
                            `${failedCount} Webpage${failedCount !== 1 ? 's' : ''} failed to delete`,
                        );
                    }

                    return { webpage_ids: data.deleted_webpage_ids };
                })
                .catch((err) => {
                    if (err.response === undefined) {
                        toast.error(`Failed to delete webpages.`);
                    }
                });
        },

        get_selected_source() {
            const selectedSourceIds = Object.values(this.selected_sources).flat();

            const expertSources = this.get_flattened_source_data();

            const sourcesToDelete = selectedSourceIds
                .map((sourceId) => {
                    return expertSources.find((source) => source.id === sourceId);
                })
                // filter out webpages that are part of a website that is also selected for deletion
                .filter(
                    (source) =>
                        !(
                            source === undefined ||
                            (source.type === 'webpage' &&
                                selectedSourceIds.includes(source.website_id))
                        ),
                );
            const documents = sourcesToDelete.filter((source) => source.type === 'document');
            const websites = sourcesToDelete.filter((source) => source.type === 'website');
            const webpages = sourcesToDelete.filter(
                (source) => source.type === 'webpage' || source.type === 'confluence_page',
            );
            return { documents, websites, webpages };
        },

        async delete_selected_sources() {
            const { documents, websites, webpages } = this.get_selected_source();
            if (documents.length > 0) {
                await this.delete_multiple_documents(documents.map((doc) => doc.id));
            }
            if (webpages.length > 0) {
                await this.delete_multiple_webpages(webpages.map((page) => page.id));
            }

            const sourceDeletionPromises = websites.map((source) => {
                return this.delete_website(source.id, source.name);
            });
            await Promise.allSettled(sourceDeletionPromises);
            this.selected_sources = {};
            await this.refresh_expert(this.expert_id);
            this.update_dashboard_expert();
        },

        async augment_sources(augmentation_data_labels, augmentation_overwrite, only_language) {
            const { documents, websites, webpages } = this.get_selected_source();
            return await axios
                .post(`/expert/augmentation/augment_knowledge`, {
                    expert_id: this.expert_id,
                    website_ids: websites.map((site) => site.id),
                    webpage_ids: webpages.map((page) => page.id),
                    document_ids: documents.map((doc) => doc.id),
                    overwrite: augmentation_overwrite,
                    only_language: only_language,
                })
                .then((response) => {
                    toast.success('Knowledge Augmentation Process Started');

                    this.add_process({
                        id: `${this.expert_id}-augmentation-${Date.now()}`,
                        process_id: response.data['process_id'],
                        type: 'augmentation-process',
                        init_message: `Augmentation process queued...`,
                        connection_url: response.data['ws_token'].url,
                        stoppable: true,
                    });
                })
                .catch((err) => {
                    if (err.response === undefined) {
                        toast.error(`Failed to augment sources.`);
                    }
                });
        },

        get_flattened_source_data() {
            const webSources = [];

            this.loaded_expert?.websites.forEach(({ webpages, id: website_id, domain }) => {
                webpages.forEach((webpage) => {
                    webSources.push({
                        id: webpage.id,
                        type: 'webpage',
                        name: webpage.url,
                        website_id,
                    });
                });
                webSources.push({
                    id: website_id,
                    type: 'website',
                    name: domain,
                });
            });

            const documentSources = this.loaded_expert?.documents.map((doc) => ({
                id: doc.id,
                type: 'document',
                name: doc.document_name,
            }));

            return [...webSources, ...documentSources];
        },
    },
});

export { useExpertStore, Status };
